import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect
} from 'react';
import axios from '../services/api';
import ENDPOINTS from '../constants/endpoints';
import {Outlet, useLocation, useNavigate} from "react-router-dom";
import {ROUTES} from "../constants/PathRoutes";
import {getRoutePermission, getAllPermissions} from "../constants/permissions";
import {message, Spin} from "antd";
import ProtectedAdminRoute from "../components/ProtectedAdminRoute";

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
    const context = useContext(AdminAuthContext);
    if (!context) {
        throw new Error('useAdminAuth deve ser usado dentro de AdminAuthProvider');
    }
    return context;
};

export const AdminAuthProvider = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation()

    const fetchMe = useCallback(async (thorwError = false) => {
        try {
            const response = await axios.get(ENDPOINTS.AUTH_ME);
            const userData = response.data;

            if (userData.type === 'Staff') {
                setUser(userData);
            }
        } catch (error) {
            setUser(null);
            if (thorwError) {
                throw Error('')
            }
        }
    }, []);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                await fetchMe(true);
                if (location.pathname === ROUTES.ADMIN) {
                    navigate(ROUTES.ADMIN_DASHBOARD)
                }

            } catch (error) {
                if (location.pathname !== ROUTES.ADMIN) {
                    message.warning('Você não possui um usuário logado');
                }
                navigate(ROUTES.ADMIN, {replace: true})
            } finally {
                setLoading(false);
            }

        };
        checkAuth();
    }, [fetchMe]);

    const login = async (username, password) => {
        setLoading(true);
        try {
            await axios.post(ENDPOINTS.AUTH_LOGIN_STAFF, {username, password});
            await fetchMe();
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        axios.post(ENDPOINTS.AUTH_LOGOUT).catch(() => {});
        setUser(null);
    };

    const isAuthenticated = () => {
        return !!user && user.type === 'Staff';
    };

    // Verifica se o usuário tem uma permissão específica
    const hasPermission = useCallback((permission) => {
        if (!user?.permissions) return false;
        return user.permissions.includes(permission);
    }, [user]);

    // Verifica se o usuário tem acesso a uma rota
    const hasRoutePermission = useCallback((pathname) => {
        const requiredPermission = getRoutePermission(pathname);
        if (!requiredPermission) return true; // Rota não requer permissão
        return hasPermission(requiredPermission);
    }, [hasPermission]);

    const value = {
        user,
        login,
        logout,
        isAuthenticated,
        hasPermission,
        hasRoutePermission,
        loading,
        fetchMe,
    };
    return (
        <AdminAuthContext.Provider value={value}>
            <ProtectedAdminRoute><Outlet/></ProtectedAdminRoute>
        </AdminAuthContext.Provider>
    );
};